import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Control, FieldValues, Path, RegisterOptions } from 'react-hook-form';

import type { VNFieldHelpProps } from '@/components/VNFieldHelp';
import { pickFieldHelpProps } from '@/components/VNFieldHelp/normalizeFieldHelpProps';

import { AutocompleteContext, AutocompleteDisplay, AutocompleteList } from './_autocompleteSlots';
import type { DisplaySlotProps, DropdownOption, FilterOption, ListSlotProps, WithMinSelections } from './types';
import VNDropdownBase from './VNDropdownBase';
import VNDropdownRhf from './VNDropdownRhf';

function getLiveDelay(query: string): number {
  if (query.length === 0) return 0;
  if (query.length === 1) return 2000;
  if (query.length === 2) return 1000;
  if (query.length === 3) return 500;
  return 250;
}

// ─── Shared props ─────────────────────────────────────────────────────────────

interface SharedProps extends VNFieldHelpProps {
  options: DropdownOption[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  displayComponent?: React.ComponentType<DisplaySlotProps>;
  listComponent?: React.ComponentType<ListSlotProps>;
  disableBorder?: boolean;
  compact?: boolean;
  popoverWidth?: number;
  popoverMinHeight?: number;
  className?: string;
  searchable?: 'autocomplete' | 'inset' | 'live';
  filterOptions?: FilterOption[];
  liveSearchFn?: (query: string) => Promise<DropdownOption[]>;
  /**
   * Minimum number of options that must remain selected (multi-select only).
   * When all chips are removed the first N options are automatically restored
   * after a short delay.
   *
   * Use this when `listComponent` is a closure (e.g. wrapping `VNDropdownOUOptions`
   * to pass the `link` prop), because closure functions cannot carry the static
   * `minSelections` marker that component slots normally use for auto-detection.
   *
   * @example
   * // Without link — auto-detected from VNDropdownOUOptions.minSelections
   * listComponent={VNDropdownOUOptions}
   *
   * // With link — must be passed explicitly
   * listComponent={(props) => <VNDropdownOUOptions {...props} link={goToOU} />}
   * minSelections={1}
   */
  minSelections?: number;
  /**
   * When true (default), honor `minSelections` / list-slot markers that auto-select
   * the current OU (or first N options) when the selection is empty.
   * Set to false to allow a fully empty selection (e.g. draft Audience with no OUs).
   */
  requireSelection?: boolean;
  /**
   * Fallback options for display of pre-selected values that may not be in the live-search
   * results yet. Passed through to VNDropdownBase as `seedOptions`.
   */
  seedOptions?: DropdownOption[];
  /**
   * When true, render a small clear button inside the trigger (left of the caret) that
   * wipes the entire selection in one click. Visible only when at least one value is selected.
   * Per-chip delete in multi-select still works.
   */
  allowClear?: boolean;
}

// ─── RHF mode — name + control ────────────────────────────────────────────────

interface VNDropdownRhfMode<TFieldValues extends FieldValues = FieldValues> extends SharedProps {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  value?: never;
  onChange?: never;
  onBlur?: never;
  error?: never;
}

// ─── Standalone controlled mode — value + onChange ────────────────────────────

interface VNDropdownStandaloneMode extends SharedProps {
  name?: never;
  control?: never;
  rules?: never;
  value: DropdownOption['id'] | Array<DropdownOption['id']> | null;
  onChange: (value: DropdownOption['id'] | Array<DropdownOption['id']> | null) => void;
  onBlur?: () => void;
  error?: string;
}

export type VNDropdownProps<TFieldValues extends FieldValues = FieldValues> =
  | VNDropdownRhfMode<TFieldValues>
  | VNDropdownStandaloneMode;

const VNDropdown = <TFieldValues extends FieldValues = FieldValues>(props: VNDropdownProps<TFieldValues>) => {
  const [query, setQuery] = useState('');
  const selectFocusedRef = useRef<(() => void) | null>(null);

  const [liveOptions, setLiveOptions] = useState<DropdownOption[]>([]);
  const [isLiveLoading, setIsLiveLoading] = useState(false);
  const liveRequestIdRef = useRef(0);
  const liveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    options,
    label,
    placeholder,
    disabled,
    multiple,
    displayComponent,
    listComponent,
    disableBorder,
    searchable,
    compact,
    popoverWidth,
    popoverMinHeight,
    className,
    filterOptions,
    liveSearchFn,
    minSelections: minSelectionsProp,
    requireSelection = true,
    seedOptions,
    allowClear,
  } = props;

  const fieldHelpProps = pickFieldHelpProps(props);

  const resolvedDisplay = searchable === 'autocomplete' || searchable === 'live' ? AutocompleteDisplay : displayComponent;
  const resolvedList = searchable === 'autocomplete' || searchable === 'live' ? AutocompleteList : listComponent;

  // Prefer the explicit prop; fall back to the static marker on the list component.
  // The fallback is only reliable when listComponent is the component itself (not a closure).
  // requireSelection=false disables auto-restore even when VNDropdownOUOptions.minSelections is set.
  const minSelections = requireSelection
    ? minSelectionsProp ?? (listComponent as React.ComponentType<ListSlotProps> & WithMinSelections)?.minSelections
    : 0;

  const contextValue = useMemo(() => ({ query, setQuery, selectFocusedRef, listComponent }), [query, listComponent]);

  useEffect(() => {
    if (!liveSearchFn) return undefined;
    if (liveDebounceRef.current !== null) {
      clearTimeout(liveDebounceRef.current);
      liveDebounceRef.current = null;
    }
    const requestId = ++liveRequestIdRef.current;
    const delay = getLiveDelay(query);
    // Show spinner immediately — even while waiting for the debounce delay.
    setIsLiveLoading(true);
    const execute = async () => {
      liveDebounceRef.current = null;
      try {
        const results = await liveSearchFn(query);
        if (liveRequestIdRef.current === requestId) setLiveOptions(results);
      } catch {
        // silently ignore
      } finally {
        if (liveRequestIdRef.current === requestId) setIsLiveLoading(false);
      }
    };
    if (delay === 0) {
      execute();
    } else {
      liveDebounceRef.current = setTimeout(execute, delay);
    }
    return () => {
      if (liveDebounceRef.current !== null) {
        clearTimeout(liveDebounceRef.current);
        liveDebounceRef.current = null;
      }
    };
  }, [query, liveSearchFn]);

  const sharedSlotProps = useMemo(
    () => ({
      options: liveSearchFn ? liveOptions : options,
      label,
      placeholder,
      disabled,
      multiple,
      displayComponent: resolvedDisplay,
      listComponent: resolvedList,
      disableBorder,
      compact,
      popoverWidth,
      popoverMinHeight,
      className,
      insetSearch: searchable === 'inset',
      minSelections,
      filterOptions,
      isLiveLoading: liveSearchFn ? isLiveLoading : undefined,
      seedOptions,
      allowClear,
      ...fieldHelpProps,
    }),
    [
      options,
      liveOptions,
      liveSearchFn,
      isLiveLoading,
      label,
      placeholder,
      disabled,
      multiple,
      resolvedDisplay,
      resolvedList,
      disableBorder,
      compact,
      popoverWidth,
      popoverMinHeight,
      className,
      searchable,
      minSelections,
      filterOptions,
      seedOptions,
      allowClear,
      fieldHelpProps,
    ]
  );

  const inner =
    props.control !== undefined ? (
      <VNDropdownRhf
        {...sharedSlotProps}
        name={(props as VNDropdownRhfMode<TFieldValues>).name}
        control={(props as VNDropdownRhfMode<TFieldValues>).control}
        rules={(props as VNDropdownRhfMode<TFieldValues>).rules}
      />
    ) : (
      <VNDropdownBase
        {...sharedSlotProps}
        value={(props as VNDropdownStandaloneMode).value}
        onChange={(props as VNDropdownStandaloneMode).onChange}
        onBlur={(props as VNDropdownStandaloneMode).onBlur}
        error={(props as VNDropdownStandaloneMode).error}
      />
    );

  return searchable === 'autocomplete' || searchable === 'live' ? (
    <AutocompleteContext.Provider value={contextValue}>{inner}</AutocompleteContext.Provider>
  ) : (
    inner
  );
};

export default VNDropdown;
